import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Phone } from "lucide-react";
import { FLOATING } from "../constants/testIds";
import { useSiteConfig } from "../lib/siteConfig";
import { openWhatsApp, callPhone, defaultWhatsAppMessage } from "../lib/whatsapp";
import { WhatsAppIcon } from "./Header";

// Keep fixed actions away from booking fields, dialogs and footer links.
// Observe lazy-loaded forms as well as the forms already on the page.
export function useFixedActionsVisible() {
    const { pathname } = useLocation();
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        setVisible(false);
        if (typeof IntersectionObserver === "undefined") return undefined;
        const watched = new Set();
        const blocking = new Set();
        const update = () => setVisible(blocking.size === 0);
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(({ target, isIntersecting, boundingClientRect }) => {
                if (isIntersecting && boundingClientRect.width > 0 && boundingClientRect.height > 0) blocking.add(target);
                else blocking.delete(target);
            });
            update();
        });
        const sync = () => {
            watched.forEach((target) => {
                if (!target.isConnected) {
                    observer.unobserve(target);
                    watched.delete(target);
                    blocking.delete(target);
                }
            });
            document.querySelectorAll('form, footer, [role="dialog"], #fare-calculator').forEach((target) => {
                if (!watched.has(target)) {
                    watched.add(target);
                    const rect = target.getBoundingClientRect();
                    if (rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight) blocking.add(target);
                    observer.observe(target);
                }
            });
            update();
        };
        const mutations = new MutationObserver(sync);
        mutations.observe(document.body, { childList: true, subtree: true });
        sync();
        return () => {
            observer.disconnect();
            mutations.disconnect();
        };
    }, [pathname]);

    return visible;
}

export default function FloatingButtons() {
    const config = useSiteConfig();
    const visible = useFixedActionsVisible();
    if (!visible) return null;

    return (
        <div className="fixed bottom-5 right-4 z-30 hidden flex-col items-center gap-2 md:flex" data-testid="floating-contact-actions">
            <button
                type="button"
                data-testid={FLOATING.call}
                onClick={() => callPhone(config.phone, "floating")}
                className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-brand-navy text-white shadow-brand transition-transform hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-gold"
                aria-label="Call VVPSS"
                title="Call VVPSS"
            >
                <Phone className="h-5 w-5" />
            </button>
            <button
                type="button"
                data-testid={FLOATING.whatsapp}
                onClick={() => openWhatsApp(config.whatsapp, defaultWhatsAppMessage(), "floating")}
                className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-brand-whatsapp text-white shadow-brand transition-transform hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-gold"
                aria-label="Enquire on WhatsApp"
                title="Enquire on WhatsApp"
            >
                <WhatsAppIcon className="h-5 w-5" />
            </button>
        </div>
    );
}
